import React from 'react';
import Fade from '@mui/material/Fade';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider, StyledEngineProvider, createTheme, alpha } from '@mui/material/styles';
import { COLORS } from '../../common/colors';
import createEmotionCache from '../../common/createEmotionCache';

// Falls back to a fresh cache if a caller doesn't pass one (there's no
// wrong answer for callers that don't care about SSR style extraction —
// only src/entry-server.jsx needs to control the cache lifecycle itself).
const defaultCache = createEmotionCache();

// MUI theme + StyledEngineProvider/ThemeProvider wrapping, shared by the
// client entry (src/index.jsx, inside BrowserRouter) and the SSR entry
// (src/entry-server.jsx, inside StaticRouter) — Router and Helmet stay out
// of this component since those differ between the two (StaticRouter needs
// a location/context prop server-side; HelmetProvider needs a context prop
// for extraction server-side).
const theme = createTheme();
const v5Theme = createTheme(theme, {
  palette: {
    ...COLORS,
    success: {
      ...COLORS.primary
    },
    info: {
      ...COLORS.secondary
    },
    surface: {
      ...COLORS.surface
    },
    "default": {
      main: COLORS.surface.light,
      dark: COLORS.surface.dark,
      light: COLORS.secondary['40']
    },
  },
  // Heading scale from the Webflow community-site design
  // (css/ocl-community-site-8651cd.webflow.css .heading-style-*).
  typography: {
    h1: { fontSize: '3.5rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '3rem', fontWeight: 700, lineHeight: 1.2 },
    h3: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
    h4: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.3 },
    h5: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.4 },
    h6: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.4 },
    button: { fontSize: '1rem', fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        leaveDelay: 300,
        slots: {
          transition: Fade,
        },
        slotProps: {
          transition: { timeout: 300 },
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small'
      },
      styleOverrides: {
        root: {
          background: COLORS.primary.contrastText,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: COLORS.surface.contrastText
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: 'rgba(94, 92, 113, 0.2)'
          }
        }
      }
    },
    MuiButton: {
      // The ring below marks keyboard focus. MUI's focus ripple, a circle of
      // the text color at 30%, would take the label under 4.5:1 inside it
      // (OpenConceptLab/ocl_issues#2970). The click ripple stays.
      defaultProps: {
        disableFocusRipple: true,
      },
      // Webflow buttons are pills: border-radius 50px, .5rem 1.25rem (small).
      styleOverrides: {
        root: {
          borderRadius: '50px',
          padding: '0.5rem 1.25rem',
          // index.scss's keyboard focus ring, again. MUI gives a filled
          // button's .Mui-focusVisible its own box-shadow (none with
          // disableElevation, an elevation shadow without), which outranks
          // the site-wide :focus-visible rule (ocl_online#225). Theme
          // overrides come after MUI's own styles, so this one wins.
          "&.Mui-focusVisible": {
            boxShadow: '0 0 0 2px var(--focus-ring-inner), 0 0 0 4px var(--focus-ring-outer)',
          },
        }
      },
      variants: [
        {
          props: { variant: "contained", color: COLORS.surface.main },
          style: {
            color: theme.palette.getContrastText(COLORS.surface.n96)
          }
        },
        {
          props: { variant: "outlined", color: COLORS.surface.main },
          style: {
            color: theme.palette.text.primary,
            borderColor:
                    theme.palette.mode === "light"
                    ? "rgba(0, 0, 0, 0.23)"
                    : "rgba(255, 255, 255, 0.23)",
            "&.Mui-disabled": {
              border: `1px solid ${theme.palette.action.disabledBackground}`
            },
            "&:hover": {
              borderColor:
                      theme.palette.mode === "light"
                      ? "rgba(0, 0, 0, 0.23)"
                      : "rgba(255, 255, 255, 0.23)",
              backgroundColor: alpha(
                theme.palette.text.primary,
                theme.palette.action.hoverOpacity
              )
            }
          }
        },
        {
          props: { color: COLORS.surface.contrastText, variant: "text" },
          style: {
            color: theme.palette.text.primary,
            "&:hover": {
              backgroundColor: alpha(
                theme.palette.text.primary,
                theme.palette.action.hoverOpacity
              )
            }
          }
        }
      ]
    }
  }
});

const AppShell = ({ children, emotionCache = defaultCache }) => (
  <CacheProvider value={emotionCache}>
    {/*
      No `injectFirst` here (it was on the original StyledEngineProvider
      pre-prerendering): when true, MUI's StyledEngineProvider creates and
      uses its *own* internal Emotion cache unconditionally, wrapped in its
      own nested CacheProvider — which shadows this outer one for every
      descendant. All real style insertions were going into that hidden,
      unreferenced cache, so entry-server.jsx's extraction always found an
      empty one — the prerendered HTML shipped zero CSS despite classNames
      being generated correctly. `injectFirst` exists to let non-MUI CSS
      (e.g. a plain .css/.scss stylesheet) beat MUI's specificity;
      index.scss only sets base `body` rules with nothing that needs to
      win a specificity fight against MUI components, so it isn't needed.
    */}
    <StyledEngineProvider>
      <ThemeProvider theme={v5Theme}>
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  </CacheProvider>
);

export default AppShell;
